
import { supabase } from '@/integrations/supabase/client';
import type { LearningPath, Course, Lesson, UserLearningProgress } from '@/types/learning';
import challengeService from './challengeService';

export const learningService = {
  async getLearningPaths(): Promise<LearningPath[]> {
    const { data, error } = await supabase
      .from('learning_paths')
      .select('*')
      .eq('is_active', true)
      .order('sort_order');

    if (error) {
      console.error('Error fetching learning paths:', error);
      return [];
    }

    return data || [];
  },

  async getCoursesByPath(pathId: string): Promise<Course[]> {
    const { data, error } = await supabase
      .from('courses')
      .select('*')
      .eq('learning_path_id', pathId)
      .order('sort_order');

    if (error) {
      console.error('Error fetching courses:', error);
      return [];
    }

    return data || [];
  },

  async getLessonsByCourse(courseId: string): Promise<Lesson[]> {
    const { data, error } = await supabase
      .from('lessons')
      .select('*')
      .eq('course_id', courseId)
      .order('sort_order');

    if (error) {
      console.error('Error fetching lessons:', error);
      return [];
    }

    return (data || []) as Lesson[];
  },

  async getUserProgress(userId: string): Promise<UserLearningProgress[]> {
    const { data, error } = await supabase
      .from('user_learning_progress')
      .select('*')
      .eq('user_id', userId);

    if (error) {
      console.error('Error fetching user progress:', error);
      return [];
    }

    return data || [];
  },

  async completeLesson(lessonId: string, score: number = 100): Promise<boolean> {
    const { error } = await supabase.rpc('complete_lesson', {
      p_user_id: (await supabase.auth.getUser()).data.user?.id ?? '',
      p_lesson_id: lessonId,
      p_score: score,
      p_time_spent: 5
    });

    if (error) {
      console.error('Error completing lesson:', error);
      return false;
    }

    // Update challenge progress
    await challengeService.updateChallengeProgress('complete_lesson', 1);

    return true;
  },

  async getLearningLeaderboard(): Promise<any[]> {
    // First get the learning progress data
    const { data: progressData, error: progressError } = await supabase
      .from('user_learning_progress')
      .select('user_id, total_xp_earned, learning_streak')
      .order('total_xp_earned', { ascending: false })
      .limit(50);

    if (progressError) {
      console.error('Error fetching learning progress:', progressError);
      return [];
    }

    if (!progressData || progressData.length === 0) {
      return [];
    }

    // Get user profiles separately
    const userIds = progressData.map(p => p.user_id);
    const { data: profilesData, error: profilesError } = await supabase
      .from('user_profiles')
      .select('id, username, full_name, avatar_url, avatar_background')
      .in('id', userIds);

    if (profilesError) {
      console.error('Error fetching user profiles:', profilesError);
      return [];
    }

    // Combine the data
    return progressData.map((progress, index) => {
      const profile = profilesData?.find(p => p.id === progress.user_id);
      return {
        id: progress.user_id,
        rank: index + 1,
        username: profile?.username || 'Unknown',
        full_name: profile?.full_name || 'Unknown User',
        avatar_url: profile?.avatar_url || null,
        avatar_background: profile?.avatar_background || 'white',
        total_xp: progress.total_xp_earned,
        learning_streak: progress.learning_streak
      };
    }) || [];
  },

  async getLearningAchievements(): Promise<any[]> {
    const { data, error } = await supabase
      .from('learning_achievements')
      .select('*')
      .order('category, requirement_value');

    if (error) {
      console.error('Error fetching learning achievements:', error);
      return [];
    }

    return data || [];
  },

  async getUserLearningAchievements(userId: string): Promise<any[]> {
    const { data, error } = await supabase
      .from('user_learning_achievements')
      .select(`
        *,
        learning_achievements (*)
      `)
      .eq('user_id', userId);

    if (error) {
      console.error('Error fetching user learning achievements:', error);
      return [];
    }

    return data?.map(ua => ({
      ...ua.learning_achievements,
      unlocked_at: ua.unlocked_at
    })) || [];
  },

  async checkAndUnlockAchievements(userId: string): Promise<any[]> {
    // Get user's learning progress
    const { data: progress } = await supabase
      .from('user_learning_progress')
      .select('*')
      .eq('user_id', userId);

    const { data: lessonProgress } = await supabase
      .from('user_lesson_progress')
      .select('*')
      .eq('user_id', userId)
      .eq('is_completed', true);

    if (!progress || !lessonProgress) return [];

    // Get all achievements
    const allAchievements = await this.getLearningAchievements();
    
    // Get user's current achievements
    const userAchievements = await this.getUserLearningAchievements(userId);
    const unlockedIds = new Set(userAchievements.map((ua: any) => ua.id));

    const newlyUnlocked: any[] = [];

    // Check each achievement
    for (const achievement of allAchievements) {
      if (unlockedIds.has(achievement.id)) continue;

      let shouldUnlock = false;

      switch (achievement.requirement_type) {
        case 'lessons_completed':
          shouldUnlock = lessonProgress.length >= achievement.requirement_value;
          break;
        case 'paths_completed':
          const completedPaths = progress.filter((p: any) => p.completion_percentage === 100);
          shouldUnlock = completedPaths.length >= achievement.requirement_value;
          break;
        case 'total_xp':
          const totalXp = progress.reduce((sum: number, p: any) => sum + (p.total_xp_earned || 0), 0);
          shouldUnlock = totalXp >= achievement.requirement_value;
          break;
        case 'streak_days':
          const maxStreak = Math.max(...progress.map((p: any) => p.learning_streak || 0));
          shouldUnlock = maxStreak >= achievement.requirement_value;
          break;
      }

      if (shouldUnlock) {
        const { error } = await supabase
          .from('user_learning_achievements')
          .insert({
            user_id: userId,
            achievement_id: achievement.id
          });

        if (!error) {
          newlyUnlocked.push(achievement);
          
          // Award XP for unlocking achievement
          if (achievement.xp_reward > 0) {
            await supabase.rpc('add_xp_and_log_activity', {
              p_user_id: userId,
              p_amount: achievement.xp_reward,
              p_activity_type: 'achievement_unlocked',
              p_activity_data: { achievement_id: achievement.id, achievement_name: achievement.name }
            });
          }
        }
      }
    }

    return newlyUnlocked;
  },

  async getQuizQuestions(lessonId: string): Promise<any[]> {
    const { data, error } = await supabase
      .from('quiz_questions')
      .select('*')
      .eq('lesson_id', lessonId)
      .order('sort_order');

    if (error) {
      console.error('Error fetching quiz questions:', error);
      return [];
    }

    return data || [];
  },

  async submitQuizAnswer(userId: string, lessonId: string, score: number, livesLost: number, mistakes: any[]): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('user_lesson_progress')
        .update({
          quiz_score: score,
          lives_lost: livesLost,
          mistakes: mistakes
        })
        .eq('user_id', userId)
        .eq('lesson_id', lessonId);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error submitting quiz answer:', error);
      return false;
    }
  }
};
